// InfoUI.js — the SoS teaching affordance, shared by the node pane and the table.
// InfoDot: a small (i) next to any taxonomy term. Hover shows the short definition;
//          clicking (the dot or "Open guide →") opens the full article in WikiDrawer.
// WikiDrawer: a right-side drawer rendering a wikiContent article. Escape closes.
//
// This mirrors the legacy D&E wiki's hover→popover→full-guide flow, rebuilt for SoS.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LENS_META, AXES_BY_LENS, wikiArticle } from './wikiContent';
import { AXIS_INFO } from './axisInfo';
import { BAND_STYLE } from './verdictModel';   // band chips read their colours from the model

const NAVY = '#14536e';

// A small round D/E/R lens badge.
function LensBadge({ lens, size = 22 }) {
  const m = LENS_META[lens];
  if (!m) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      width: size, height: size, borderRadius: '50%', background: m.color, color: '#fff',
      fontSize: size * 0.5, fontWeight: 800 }}>{m.badge}</span>
  );
}

// The always-present index: jump to the overview, any lens, or any of the 12 axes — colour-coded,
// current entry highlighted. This is what makes the drawer fully interconnected (no exit needed).
function FrameworkIndex({ current, onNavigate }) {
  const go = (a) => a && onNavigate && onNavigate(a);
  const curId = current && current.id;
  return (
    <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #eef2f6' }}>
      <div style={{ color: NAVY, fontWeight: 800, fontSize: '.92rem', marginBottom: 10 }}>Explore the framework</div>

      <button type="button" onClick={() => go(wikiArticle('overview', 'framework'))}
        className="sos-wiki-chip" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '8px 10px', borderRadius: 9, border: '1px solid #e2e8f0',
          background: current && current.kind === 'overview' ? '#eef4f7' : '#fff', cursor: 'pointer', marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>🗺</span>
        <span style={{ fontWeight: 700, color: NAVY, fontSize: '.88rem' }}>How the Layered Read reads a scene</span>
      </button>

      {['Design', 'Execution', 'Involvement'].map(L => {
        const m = LENS_META[L];
        return (
          <div key={L} style={{ marginBottom: 12 }}>
            <button type="button" onClick={() => go(wikiArticle('lens', m.id))}
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '5px 6px', borderRadius: 8, borderLeft: `3px solid ${m.color}`,
                background: current && current.kind === 'lens' && current.id === m.id ? m.tint : 'transparent' }}>
              <LensBadge lens={L} size={20} />
              <span style={{ fontWeight: 800, color: m.color, fontSize: '.9rem' }}>{L}</span>
              <span style={{ color: '#7a8a94', fontSize: '.8rem' }}>· {m.blurb}</span>
            </button>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 28, marginTop: 6 }}>
              {AXES_BY_LENS[L].map(ax => {
                const active = curId === ax;
                return (
                  <button key={ax} type="button" onClick={() => go(wikiArticle('axis', ax))}
                    className="sos-wiki-chip"
                    style={{ cursor: 'pointer', fontSize: '.8rem', fontWeight: 600, padding: '3px 10px',
                      borderRadius: 20, border: `1.5px solid ${active ? m.color : '#dbe3e8'}`,
                      background: active ? m.color : '#fff', color: active ? '#fff' : '#3f5a6b' }}>
                    {AXIS_INFO[ax].label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// The (i) marker. `article` = {label, short, body}. `onOpen(article)` opens the drawer.
export function InfoDot({ article, onOpen, size = 14, children = null }) {
  const [pop, setPop] = useState(null); // {x,y} when hovering
  const hideTimer = useRef(null);
  // Measured popover width, so it can be kept inside the viewport — see the popover's own note.
  // ⚠️ Both hooks MUST stay above the `if (!article)` early return, or they run conditionally.
  const popRef = useRef(null);
  const [popW, setPopW] = useState(0);
  useLayoutEffect(() => {
    if (!pop || !popRef.current) return;
    const w = popRef.current.getBoundingClientRect().width;
    setPopW(prev => (Math.abs(prev - w) < 1 ? prev : w));   // guard: no-op re-render loop
  }, [pop]);
  if (!article) return null;

  const show = (e) => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    const r = e.currentTarget.getBoundingClientRect();
    setPop({ x: r.left + r.width / 2, y: r.bottom });
  };
  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setPop(null), 140);
  };
  const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };

  return (
    <>
      {children != null ? (
        <span onMouseEnter={show} onMouseLeave={scheduleHide}
          onClick={(e) => { e.stopPropagation(); setPop(null); onOpen && onOpen(article); }}
          role="button" tabIndex={0} aria-label={`What is ${article.label}?`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          {children}
          <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', border: `1px solid ${NAVY}55`, background: '#fff', color: NAVY, fontSize: size * 0.62, fontWeight: 700, lineHeight: 1, padding: 0, fontStyle: 'italic' }}>i</span>
        </span>
      ) : (
      <button type="button"
        onMouseEnter={show} onMouseLeave={scheduleHide}
        onClick={(e) => { e.stopPropagation(); setPop(null); onOpen && onOpen(article); }}
        aria-label={`What is ${article.label}?`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size, height: size, borderRadius: '50%', border: `1px solid ${NAVY}55`,
          background: '#fff', color: NAVY, fontSize: size * 0.62, fontWeight: 700,
          lineHeight: 1, cursor: 'pointer', padding: 0, fontStyle: 'italic',
        }}>i</button>
      )}
      {pop && (() => {
        // ⚠️ `width: max-content` + a MEASURED clamp, not `left` + `translateX(-50%)` alone.
        // A position:fixed box with only `left` set is shrink-to-fit against `viewportWidth - left`,
        // so this popover's width was decided by WHERE IT SAT rather than by its content or its
        // maxWidth. Measured on a 375px viewport (2026-09-04): the rightmost dot sat at x=215 and
        // the popover came out exactly 160px wide — i.e. 375-215 — instead of 260, rendering as a
        // tall narrow column. The transform cannot fix it: transforms apply after layout, so the
        // width is already settled. Same defect and same fix as the icicle's hover tooltip; Tip.js
        // has solved it a third way (it measures its node parked off-screen at left:-9999px).
        const half = popW / 2;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
        const left = (vw && popW) ? Math.min(Math.max(pop.x, half + 8), vw - half - 8) : pop.x;
        return (
        <div ref={popRef} onMouseEnter={cancelHide} onMouseLeave={scheduleHide}
          style={{
            position: 'fixed', left, top: pop.y + 6, transform: 'translateX(-50%)',
            width: 'max-content', maxWidth: 'min(260px, calc(100vw - 24px))',
            zIndex: 60, background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 12, boxShadow: '0 8px 28px rgba(15,23,42,.16)', padding: '11px 13px',
          }}>
          <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '.9rem', marginBottom: 4 }}>{article.label}</div>
          <div style={{ color: '#334155', fontSize: '.84rem', lineHeight: 1.45 }}>{article.short}</div>
          {/* NO Bootstrap `.btn-link` on the button below. public/css/style-analyze.css:572 restyled
              a bare `.btn-link` into a 32x32 teal (#0B6862) CIRCLE for the pricing-plan list, and
              that sheet loads on /analyze_this — so this button rendered as a solid dot with the
              words "Open guide →" squashed around it. Invisible on the Sos2 app pages, which never
              load that sheet, which is why it survived. The stylesheet is now scoped with
              :not(.btn); these inline styles are belt-and-braces. Fixed 2026-08-13. */}
          {article.body && article.body.length > 0 && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setPop(null); onOpen && onOpen(article); }}
              style={{ color: NAVY, fontWeight: 700, fontSize: '.78rem', background: 'none',
                       border: 0, padding: 0, marginTop: 8, cursor: 'pointer', width: 'auto',
                       height: 'auto', borderRadius: 0, display: 'inline', textDecoration: 'none',
                       whiteSpace: 'nowrap' }}>Open guide →</button>
          )}
        </div>
        );
      })()}
    </>
  );
}

// Right-side drawer for the full article. `article` = {label, short, body[]}; null = closed.
// `onNavigate(article)` (optional) lets the framework footer hop to another article in place.
// ── Article body rendering ─────────────────────────────────────────────────────────────────────
// Every article body used to render as a flat run of <p> strings, which read as a wall of grey.
// Two things fix that without giving any article the power to inject markup:
//
//   1. INLINE MARKUP inside any string — **bold** and `term`. Parsed into React elements, never
//      innerHTML, so there is no XSS surface even though these strings are authored by us.
//   2. STRUCTURED BLOCKS — a body entry may be an object instead of a string:
//        { h: 'Heading' }                  a section heading
//        { list: ['a', 'b'] }              bullets (inline markup applies inside each)
//        { bands: true }                   the four triage band chips, coloured from BAND_STYLE
//        { note: 'text' }                  an aside, tinted in the article's accent colour
//
// Existing string-only articles are unaffected — they take the string branch unchanged.

// Split on **bold** and `term`, returning React nodes. No dangerouslySetInnerHTML anywhere.
export function inlineMarkup(text) {
  if (typeof text !== 'string') return text;
  // ORDER MATTERS: the `**` alternative must precede the `*` one, or `**bold**` is consumed as an
  // empty italic. Both use [^*]+ so they can't span an adjacent marker. Single-asterisk italic was
  // added 2026-08-21 for the axis articles' "Why it matters" / grounding sections, which lean on
  // mid-sentence stress that bold renders far too heavily (understanding vs *having* a feeling).
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((seg, i) => {
    if (/^\*\*[^*]+\*\*$/.test(seg)) {
      return <strong key={i} style={{ color: '#0f172a', fontWeight: 700 }}>{seg.slice(2, -2)}</strong>;
    }
    if (/^\*[^*]+\*$/.test(seg)) {
      return <em key={i} style={{ fontStyle: 'italic' }}>{seg.slice(1, -1)}</em>;
    }
    if (/^`[^`]+`$/.test(seg)) {
      return <em key={i} style={{ fontStyle: 'normal', fontWeight: 600, color: NAVY,
        background: '#eef4f7', borderRadius: 4, padding: '1px 5px' }}>{seg.slice(1, -1)}</em>;
    }
    return seg;
  });
}

const BODY_P = { color: '#334155', fontSize: '.92rem', lineHeight: 1.65, margin: '0 0 12px' };

// Exported 2026-10-22 for the public guide on /how-layered-read-works (lrGuideApp.js), which renders
// the same articles inline instead of in the drawer.
export function WikiBlock({ block, accent }) {
  if (block == null) return null;
  if (typeof block === 'string') return <p style={BODY_P}>{inlineMarkup(block)}</p>;

  if (block.h) {
    // NO EYEBROW LABELS (house rule). This was `.82rem` uppercase with `.06em` tracking — i.e.
    // SMALLER than the `.92rem` body it labels, which is the eyebrow pattern the house style
    // bans outright: a section label must never be quieter than its own content. Now set above
    // body size, semibold, in the site navy-teal. The hairline rule stays; it does the
    // separating work that the uppercasing was being asked to do.
    return <h4 style={{ color: NAVY, fontWeight: 700, fontSize: '1.02rem', letterSpacing: '.005em',
      margin: '22px 0 9px', paddingBottom: 6,
      borderBottom: '1px solid #e8eef1' }}>{block.h}</h4>;
  }

  if (block.list) {
    return (
      <ul style={{ ...BODY_P, paddingLeft: 0, listStyle: 'none' }}>
        {block.list.map((li, i) => (
          <li key={i} style={{ position: 'relative', paddingLeft: 16, margin: '0 0 7px' }}>
            <span aria-hidden style={{ position: 'absolute', left: 0, top: '.62em', width: 5, height: 5,
              borderRadius: '50%', background: accent, opacity: .75 }} />
            {inlineMarkup(li)}
          </li>
        ))}
      </ul>
    );
  }

  if (block.bands) {
    // Read the colours straight from the model so the chips can never drift from the chart.
    const rows = [
      ['keep',   'Keep',    'All three groups solid.'],
      ['polish', 'Polish',  'Nothing broken, at least one soft.'],
      ['rework', 'Rework',  'Exactly one group broken.'],
      ['heavy',  'Rebuild', 'Two or more broken. Reads “Low on its own” at scene and sequence grain.'],
    ];
    return (
      <div style={{ margin: '0 0 14px', border: '1px solid #e8eef1', borderRadius: 10, overflow: 'hidden' }}>
        {rows.map(([key, label, desc], i) => {
          const s = BAND_STYLE[key] || {};
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 11px',
              borderTop: i ? '1px solid #f1f5f7' : 0 }}>
              <span style={{ flexShrink: 0, minWidth: 62, textAlign: 'center', padding: '2px 8px',
                borderRadius: 999, background: s.bg, color: s.fg, border: `1px solid ${s.bd}`,
                fontSize: '.74rem', fontWeight: 800 }}>{label}</span>
              <span style={{ color: '#475569', fontSize: '.84rem', lineHeight: 1.45 }}>{inlineMarkup(desc)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (block.note) {
    return (
      <p style={{ ...BODY_P, background: '#f6f9fa', borderLeft: `3px solid ${accent}`,
        borderRadius: '0 8px 8px 0', padding: '10px 12px', fontSize: '.88rem' }}>
        {inlineMarkup(block.note)}
      </p>
    );
  }

  return null;
}

export function WikiDrawer({ article, onClose, onNavigate }) {
  useEffect(() => {
    if (!article) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [article, onClose]);
  if (!article) return null;

  // lens colour (axis inherits its lens); falls back to navy for role/band/overview/derived articles
  const lensMeta = article.lens ? LENS_META[article.lens] : null;
  const accent = lensMeta ? lensMeta.color : NAVY;

  // Portal to <body> so the drawer escapes any ancestor stacking context (a parent with a
  // transform/filter would otherwise trap its z-index BELOW the site navbar, no matter how
  // high we set it). On <body> at z-index 2000 it overlays the navbar as intended.
  //
  // ⚠️ Z-INDEX (raised 2026-08-21) — 2000 was NOT enough on /view_analysis. The legacy scene
  // rail `#scene-nav-panel` is `position: fixed`, 36px wide, pinned to the right edge, and sits
  // at **z-index 199060**. The drawer is also pinned right, so the rail painted over its last
  // ~36px and silently ate a word or two off the end of every line — measured on localhost with
  // the drawer at x=1090..1470 and the rail at x=1434. It reads as sloppy wrapping rather than
  // as an overlap, which is why it survived this long. Do not lower these back toward 2000
  // without checking that rail; the sane fix (giving the rail a sane z-index) is a legacy blade
  // change with a much wider blast radius.
  return createPortal(
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.18)', zIndex: 199990 }} />
      <div role="dialog" aria-label={article.label} data-sos-wiki
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '92%', zIndex: 199999,
          background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-10px 0 30px rgba(15,23,42,.16)',
          borderTop: `4px solid ${accent}`, padding: '16px 22px', overflowY: 'auto',
        }}>
        <div className="d-flex align-items-center" style={{ marginBottom: 12, gap: 8 }}>
          {lensMeta && <LensBadge lens={article.lens} size={22} />}
          <span style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: accent }}>
            {article.kicker || 'Guide'}
          </span>
          <button type="button" onClick={onClose}
            className="btn btn-link btn-sm p-0 text-decoration-none ms-auto"
            style={{ color: NAVY, fontWeight: 700 }}>✕ Close</button>
        </div>
        <h3 style={{ color: '#0f172a', fontWeight: 800, fontSize: '1.4rem', margin: '0 0 6px' }}>{article.label}</h3>
        {article.short && <p style={{ color: '#475569', fontSize: '.95rem', lineHeight: 1.5, margin: '0 0 12px', fontWeight: 500 }}>{article.short}</p>}
        {/* score scale as a styled chip, not a buried sentence */}
        {article.scale && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 0 14px',
            padding: '4px 10px', borderRadius: 8, background: lensMeta ? lensMeta.tint : '#eef4f7',
            color: accent, fontSize: '.8rem', fontWeight: 700 }}>
            <span aria-hidden>⟂</span> Scored {article.scale}
          </div>
        )}
        {(article.body || []).map((p, i) => <WikiBlock key={i} block={p} accent={accent} />)}
        {/* fully-interconnected index — reach the overview, any lens, or any of the 12 axes from here */}
        {onNavigate && <FrameworkIndex current={article} onNavigate={onNavigate} />}
      </div>
    </>,
    document.body
  );
}
